import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import type { Channel } from '../types';
import { Button, ErrorBanner } from './Ui';

type Props = {
  channels: Channel[];
  onChanged: (channels: Channel[]) => void;
};

/**
 * Список подключённых каналов. Их может быть несколько — типовой сценарий:
 * тестовый канал для проверки поста и основной для публикации.
 */
export function ChannelSetup({ channels, onChanged }: Props) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const run = async (action: () => Promise<Channel[]>) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await action());
      setValue('');
      setAdding(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не получилось, попробуй ещё раз');
    } finally {
      setBusy(false);
    }
  };

  const showForm = adding || channels.length === 0;

  return (
    <div className="channel-card">
      <div className="channel-card__label">
        {channels.length === 0 ? 'Канал не подключён' : 'Каналы для публикации'}
      </div>

      {channels.length > 0 && (
        <ul className="channel-list">
          {channels.map((channel) => (
            <li className="channel-list__item" key={channel.id}>
              <div className="channel-list__body">
                <div className="channel-list__title">
                  {channel.title ?? channel.chatId}
                  {channel.isDefault && <span className="chip chip--ok">основной</span>}
                </div>
                {channel.username && (
                  <div className="muted small">@{channel.username}</div>
                )}
              </div>
              <div className="channel-list__actions">
                {!channel.isDefault && (
                  <button
                    className="link-btn small"
                    disabled={busy}
                    onClick={() => void run(() => api.setDefaultChannel(channel.id))}
                  >
                    сделать основным
                  </button>
                )}
                <button
                  className="icon-btn icon-btn--danger"
                  aria-label="Отключить канал"
                  disabled={busy}
                  onClick={() => void run(() => api.removeChannel(channel.id))}
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm ? (
        <>
          <p className="muted small">
            Добавь бота администратором в канал с правом отправки сообщений — канал
            подключится сам. Или укажи @username вручную:
          </p>
          <div className="row">
            <input
              className="input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="@my_channel"
              autoCapitalize="off"
              autoCorrect="off"
            />
            <Button
              onClick={() => void run(() => api.addChannel(value.trim()))}
              loading={busy}
              disabled={value.trim().length < 2}
            >
              Добавить
            </Button>
          </div>
          {channels.length > 0 && (
            <button className="link-btn" onClick={() => setAdding(false)}>
              отмена
            </button>
          )}
        </>
      ) : (
        <button className="link-btn" onClick={() => setAdding(true)}>
          + добавить ещё канал
        </button>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}
    </div>
  );
}
